<script setup lang="ts">
  import { MpLoadingContainer, MpText } from '@borg/ui';

  const { t } = useI18n();
  const route = useRoute();
  const organizationCard = useOrganizationCard.provide();

  const employerId = parseInt(route.params.employerId as string, 10);
  const slug = Array.isArray(route.params.slug) ? route.params.slug.join('/') : route.params.slug;

  const organizationName = computed(() => organization.value?.name ?? undefined);

  const config = useRuntimeConfig();
  const siteName = (config.public.project as any)?.title || (config.public.project as any)?.name || 'MojPosao';

  const tagTitle = computed(() => {
    const baseTitle = organizationName.value
      ? `${t('ORGANIZATION_PROFILE_PAGE.TITLE')} - ${organizationName.value}`
      : t('ORGANIZATION_PROFILE_PAGE.TITLE');
    return `${baseTitle} | ${siteName}`;
  });

  const tagImage = computed(() =>
    organization.value?.logoUrl && !!organization.value.logoUrl
      ? organization.value.logoUrl
          .replace(/-\d+x\d+x\d+./, '-600x315x85.')
          .replace(/-fit-/, '-frame-')
      : undefined,
  );
  const tagImageWidth = computed(() => (tagImage.value ? 600 : undefined));
  const tagImageHeight = computed(() => (tagImage.value ? 315 : undefined));

  const { slugifyCompanyName, getEmployerProfileUrl } = useEmployerUrl();

  // Generate canonical URL with company slug
  const canonicalUrl = computed(() => {
    if (!organization.value) return undefined;
    const { $config } = useNuxtApp();
    const baseUrl = $config.public.baseUrl || process.env.NUXT_PUBLIC_BASE_URL || 'https://mojposao.hr';
    const profilePath = getEmployerProfileUrl(employerId, organization.value.name);
    return `${baseUrl}${profilePath}`;
  });

  // todo - handle error and reload
  const { data: organization, pending: pendingOrganization } =
    await organizationRepository.get(employerId);
  const { data: jobs, pending: pendingOrganizationJobs } =
    await organizationJobsRepository.getMany(employerId);

  // Validate slug matches company name - redirect if not
  if (organization.value && slug) {
    const expectedSlug = slugifyCompanyName(organization.value.name);
    if (slug !== expectedSlug) {
      throw createError({
        statusCode: 301,
        statusMessage: 'Moved Permanently',
        data: {
          redirectTo: getEmployerProfileUrl(employerId, organization.value.name)
        }
      });
    }
  }

  useHead({
    title: tagTitle,
    link: [{ rel: 'canonical', href: canonicalUrl }],
  });

  useSeoMeta({
    ogTitle: tagTitle,
    ogImage: tagImage,
    ogImageWidth: tagImageWidth,
    ogImageHeight: tagImageHeight,
    ogUrl: canonicalUrl,
  });

  watch(
    organization,
    (value) => {
      if (value) {
        organizationCard.set(value);
      }
    },
    { immediate: true },
  );

</script>

<template>
  <MpLoadingContainer :is-loading="pendingOrganization || pendingOrganizationJobs">
    <LayoutGrid v-if="organization">
      <MpText as="h1">
        {{ organizationName }}
      </MpText>
      <WebView
        :html="organization.content"
        style="width: 100%"
      />
      <template #aside>
        <OrganizationCard />
        <OrganizationJobsCard
          v-if="jobs"
          :jobs="jobs"
        />
        <OrganizationArticles
          v-if="organization.tag"
          :tag="organization.tag"
        />
      </template>
    </LayoutGrid>
  </MpLoadingContainer>
</template>
